// CMSManagement: Sliders, packages, hotels, schedule, highlights, VIPs, venues, footer
import React, { useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import SponsorsTab from '../cms/SponsorsTab';
import ContactTab from '../cms/ContactTab';
import { Box, Typography, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody, Button, Select, MenuItem, FormControl, InputLabel, Card, CardContent, Switch, FormControlLabel } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import LoaderOverlay from '../../components/LoaderOverlay';
import Tooltip from '@mui/material/Tooltip';

const PROD_UPLOADS = 'https://backend-0xbv.onrender.com/uploads';
function fixAssetUrl(url) {
  if (!url) return url;
  return url.replace('http://localhost:5050/uploads', PROD_UPLOADS);
}

function CMSManagement() {
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editContent, setEditContent] = useState('');
  // Slider area size
  const [sliderWidth, setSliderWidth] = useState(1200);
  const [sliderHeight, setSliderHeight] = useState(420);
  // Countdown config
  const [countdownDate, setCountdownDate] = useState('2026-06-01T09:00');
  // Belt messages
  const [beltMessages, setBeltMessages] = useState([]);
  const [beltInput, setBeltInput] = useState('');
  // Slider state
  const [sliders, setSliders] = useState([]);
  const [sliderLoading, setSliderLoading] = useState(false);
  const [sliderDialog, setSliderDialog] = useState(false);
  const [sliderEdit, setSliderEdit] = useState(null); // null for add, object for edit
  const [sliderForm, setSliderForm] = useState({ title: '', desc: '', image: '', style: '' });
  const [sliderImageFile, setSliderImageFile] = useState(null);
  const [cmsItems, setCmsItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  // Schedule state
  const [schedule, setSchedule] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [scheduleEditIdx, setScheduleEditIdx] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ time: '', activity: '' });

  // Highlights state
  const [highlights, setHighlights] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(false);
  const [highlightDialog, setHighlightDialog] = useState(false);
  const [highlightEditIdx, setHighlightEditIdx] = useState(null);
  const [highlightForm, setHighlightForm] = useState({ image: '', heading: '', story: '' });

  // VIP state
  const [vips, setVips] = useState([]);
  const [vipLoading, setVipLoading] = useState(false);
  const [vipDialog, setVipDialog] = useState(false);
  const [vipEditIdx, setVipEditIdx] = useState(null);
  const [vipForm, setVipForm] = useState({ subtype: 'normal', image: '', title: '', name: '', quote: '', description: '' });

  // Venues state
  const [venues, setVenues] = useState([]);
  const [venueLoading, setVenueLoading] = useState(false);
  const [venueDialog, setVenueDialog] = useState(false);
  const [venueEditIdx, setVenueEditIdx] = useState(null);
  const [venueForm, setVenueForm] = useState({ name: '', location: '', coords: { lat: '', lng: '' }, image: '', description: '', capacity: '', tags: [], staff: [] });
  const [staffEntry, setStaffEntry] = useState({ name: '', role: '', email: '' });
  // Footer and Contact state
  const [footerForm, setFooterForm] = useState({ logo: '', text: '', links: [], social: [], gfooterHTML: '' });
  const [footerLoading, setFooterLoading] = useState(false);
  const [contactForm, setContactForm] = useState({ address: '', phone: '', email: '', intro: '', mapEmbed: '', formEnabled: true });
  const [contactLoading, setContactLoading] = useState(false);
  // Loader state (replaces Blog tab)
  const [loaderForm, setLoaderForm] = useState({ enabled: false, spinner: '', spinnerLight: '', spinnerDark: '', message: 'Loading...', backgroundColor: '#ffffff', backgroundColorLight: '#ffffff', backgroundColorDark: '#0b1220', durationMs: 800, customJS: '' });
  const [loaderLoading, setLoaderLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // About tab state
  const [aboutForm, setAboutForm] = useState({ heading: '', text: '', image: '', link: '' });
  const [aboutLoading, setAboutLoading] = useState(false);
  // Landing backgrounds
  const [landingForm, setLandingForm] = useState({ backgrounds: {} });
  const [landingLoading, setLandingLoading] = useState(false);

  // Load highlights from CMS items when tab active or cmsItems change
  useEffect(() => {
    if (tab === 2) {
      const highlightItems = (cmsItems || []).filter(i => i.type === 'highlight');
      const allHighlights = highlightItems.flatMap(i => Array.isArray(i.contentJSON?.items) ? i.contentJSON.items : []);
      setHighlights(allHighlights);
    }
    if (tab === 3) {
      const vipItems = (cmsItems || []).filter(i => i.type === 'VIP');
      const allVips = vipItems.flatMap(i => Array.isArray(i.contentJSON?.items) ? i.contentJSON.items : []);
      setVips(allVips);
    }
  }, [tab, cmsItems]);

  const openHighlightDialog = (idx = null) => {
    setHighlightEditIdx(idx);
    setHighlightForm(idx !== null ? { ...highlights[idx] } : { image: '', heading: '', story: '' });
    setHighlightDialog(true);
  };

  // Save Loader config to CMS (type: 'loader')
  const saveLoaderToBackend = async () => {
    setLoaderLoading(true);
    try {
      // Refresh latest CMS items to ensure we have current loader id
      const current = await api.get('/api/cms');
      const latestItems = current.data || current;
      const loaderItem = (latestItems || []).find(i => i.type === 'loader');
      const payload = { contentJSON: { ...loaderForm } };
      if (loaderItem && loaderItem._id) {
        await api.put(`/api/cms/${loaderItem._id}`, payload);
      } else {
        // Try create, but if server responds with 409 (already exists), fallback to update
        try {
          await api.post('/api/cms', { type: 'loader', ...payload });
        } catch (err) {
          if (err.response && err.response.status === 409) {
            // Fetch again to get id and update
            const fresh = await api.get('/api/cms');
            const freshLoader = (fresh.data || fresh).find(i => i.type === 'loader');
            if (freshLoader && freshLoader._id) {
              await api.put(`/api/cms/${freshLoader._id}`, payload);
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        }
      }
      const res = await api.get('/api/cms');
      setCmsItems(res.data || res);
      // Update local loaderForm from saved data to ensure preview matches persisted state
      const savedLoader = (res.data || res).find(i => i.type === 'loader');
      if (savedLoader) {
        const l = savedLoader.contentJSON || {};
        setLoaderForm({
          enabled: !!l.enabled,
          spinner: l.spinner || '',
          spinnerLight: l.spinnerLight || l.spinner || '',
          spinnerDark: l.spinnerDark || l.spinner || '',
          message: l.message || 'Loading...',
          backgroundColor: l.backgroundColor || '#ffffff',
          backgroundColorLight: l.backgroundColorLight || l.backgroundColor || '#ffffff',
          backgroundColorDark: l.backgroundColorDark || l.backgroundColor || '#0b1220',
          durationMs: l.durationMs || 800,
          customJS: l.customJS || ''
        });
      }
      setSnackbar({ open: true, message: 'Loader settings saved!', severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to save loader settings';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoaderLoading(false);
    }
  };
  const saveHighlightItem = () => {
    if (!highlightForm.image.trim() || !highlightForm.heading.trim() || !highlightForm.story.trim()) return;
    let newHighlights = [...highlights];
    if (highlightEditIdx !== null) {
      newHighlights[highlightEditIdx] = { ...highlightForm };
    } else {
      newHighlights.push({ ...highlightForm });
    }
    setHighlights(newHighlights);
    setHighlightDialog(false);
    setHighlightEditIdx(null);
    setHighlightForm({ image: '', heading: '', story: '' });
  };
  const deleteHighlightItem = idx => setHighlights(highlights.filter((_, i) => i !== idx));
  const saveHighlightsToBackend = async () => {
    setHighlightsLoading(true);
    try {
      const highlightItem = cmsItems.find(i => i.type === 'highlight');
      if (highlightItem) {
        await api.put(`/api/cms/${highlightItem._id}`, { contentJSON: { items: highlights } });
      } else {
        await api.post('/api/cms', { type: 'highlight', contentJSON: { items: highlights } });
      }
      const res = await api.get('/api/cms');
      setCmsItems(res.data);
      const h = (res.data || []).filter(i => i.type === 'highlight').flatMap(i => Array.isArray(i.contentJSON?.items) ? i.contentJSON.items : []);
      setHighlights(h);
      setSnackbar({ open: true, message: 'Highlights saved!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save highlights', severity: 'error' });
    } finally {
      setHighlightsLoading(false);
    }
  };

  // VIP handlers
  const openVipDialog = (idx = null) => {
    setVipEditIdx(idx);
    setVipForm(idx !== null ? { ...vips[idx] } : { subtype: 'normal', image: '', title: '', name: '', quote: '', description: '' });
    setVipDialog(true);
  };
  const saveVipItem = () => {
    if (!vipForm.title && !vipForm.name) return;
    const newVips = [...vips];
    if (vipEditIdx !== null) newVips[vipEditIdx] = { ...vipForm };
    else newVips.push({ ...vipForm });
    setVips(newVips);
    setVipDialog(false);
    setVipEditIdx(null);
    setVipForm({ subtype: 'normal', image: '', title: '', name: '', quote: '', description: '' });
  };
  // move up/down for ordering
  const moveVipUp = idx => {
    if (idx <= 0) return;
    const a = [...vips];
    const t = a[idx-1];
    a[idx-1] = a[idx];
    a[idx] = t;
    setVips(a);
  };
  const moveVipDown = idx => {
    if (idx >= vips.length - 1) return;
    const a = [...vips];
    const t = a[idx+1];
    a[idx+1] = a[idx];
    a[idx] = t;
    setVips(a);
  };
  const deleteVipItem = idx => setVips(vips.filter((_, i) => i !== idx));
  const saveVipsToBackend = async () => {
    setVipLoading(true);
    try {
      const vipItem = cmsItems.find(i => i.type === 'VIP');
      if (vipItem) {
        await api.put(`/api/cms/${vipItem._id}`, { contentJSON: { items: vips } });
      } else {
        await api.post('/api/cms', { type: 'VIP', contentJSON: { items: vips } });
      }
      const res = await api.get('/api/cms');
      setCmsItems(res.data);
      const all = (res.data || []).filter(i => i.type === 'VIP').flatMap(i => Array.isArray(i.contentJSON?.items) ? i.contentJSON.items : []);
      setVips(all);
      setSnackbar({ open: true, message: 'VIPs saved!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save VIPs', severity: 'error' });
    } finally {
      setVipLoading(false);
    }
  };

  // Venue handlers
  const openVenueDialog = (idx = null) => {
    setVenueEditIdx(idx);
    if (idx !== null) setVenueForm({ ...venues[idx] });
    else setVenueForm({ name: '', location: '', coords: { lat: '', lng: '' }, image: '', description: '', capacity: '', tags: [], staff: [] });
    setVenueDialog(true);
  };

  const saveVenueItem = async () => {
    setVenueLoading(true);
    try {
      // Normalize payload: numbers, arrays
      const payload = { ...venueForm };
      if (payload.capacity !== undefined && payload.capacity !== '') payload.capacity = Number(payload.capacity);
      if (payload.coords) {
        const lat = Number(payload.coords.lat);
        const lng = Number(payload.coords.lng);
        payload.coords = { lat: isNaN(lat) ? undefined : lat, lng: isNaN(lng) ? undefined : lng };
      }
      if (typeof payload.tags === 'string') payload.tags = payload.tags.split(',').map(t => t.trim()).filter(Boolean);
      // ensure staff is an array of simple objects
      payload.staff = Array.isArray(payload.staff) ? payload.staff.map(s => ({ name: s.name, role: s.role, email: s.email, image: s.image })) : [];

      if (payload._id) {
        await api.put(`/api/venues/${payload._id}`, payload);
        setSnackbar({ open: true, message: 'Venue updated', severity: 'success' });
      } else {
        await api.post('/api/venues', payload);
        setSnackbar({ open: true, message: 'Venue created', severity: 'success' });
      }
      // refresh list
      const res = await api.get('/api/venues');
      setVenues(res.data || []);
      setVenueDialog(false);
    } catch (err) {
      const serverMsg = err?.response?.data?.error || err?.response?.data || err.message;
      setSnackbar({ open: true, message: typeof serverMsg === 'string' ? serverMsg : 'Failed to save venue', severity: 'error' });
    } finally {
      setVenueLoading(false);
    }
  };

  const deleteVenueItem = async (id) => {
    if (!confirm('Delete this venue?')) return;
    setVenueLoading(true);
    try {
      await api.delete(`/api/venues/${id}`);
      const res = await api.get('/api/venues');
      setVenues(res.data || []);
      setSnackbar({ open: true, message: 'Venue deleted', severity: 'success' });
    } catch (err) {
      const serverMsg = err?.response?.data?.error || err?.response?.data || err.message;
      setSnackbar({ open: true, message: typeof serverMsg === 'string' ? serverMsg : 'Failed to delete venue', severity: 'error' });
    } finally {
      setVenueLoading(false);
    }
  };

  const addStaffToVenueForm = () => {
    if (!staffEntry.name.trim()) return;
    setVenueForm(f => ({ ...f, staff: [...(f.staff||[]), { ...staffEntry }] }));
    setStaffEntry({ name: '', role: '', email: '' });
  };

  const removeStaffFromVenueForm = (idx) => {
    setVenueForm(f => ({ ...f, staff: (f.staff||[]).filter((_,i)=>i!==idx) }));
  };

  const handleTabChange = (e, v) => setTab(v);
  const cmsTabs = [
    'Slider & Countdown', 'Schedule', 'Highlights', 'VIPs', 'Venues', 'Sponsors', 'Contact', 'Footer', 'Loader', 'About', 'LandingPage', 'New Page'
  ];
  // Example: fetch logo from backend for logo tab
  // const logoSrc = '/api/public/logo';

  useEffect(() => {
    setLoading(true);
    api.get('/api/cms')
      .then(res => {
        setCmsItems(res.data);
        // Load schedule from CMS items
        const sched = (res.data || []).find(i => i.type === 'schedule');
        setSchedule(sched?.contentJSON?.items || []);
      })
      .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch CMS items', severity: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  // Initialize footer and contact forms from CMS items when available
  useEffect(() => {
    if (!cmsItems) return;
    const footerItem = (cmsItems || []).find(i => i.type === 'footer');
    if (footerItem) {
      const data = footerItem.contentJSON || {};
      setFooterForm({
        logo: data.logo || data.logoUrl || '',
        text: data.text || '',
        links: Array.isArray(data.links) ? data.links : (typeof data.links === 'string' ? data.links.split('\n').map(l=>l.trim()).filter(Boolean) : []),
        social: Array.isArray(data.social) ? data.social : [],
        gfooterHTML: data.gfooterHTML || data.gfooter || ''
      });
    }
    const contactItem = (cmsItems || []).find(i => i.type === 'contact');
    if (contactItem) {
      const d = contactItem.contentJSON || {};
      setContactForm({ address: d.address || '', phone: d.phone || '', email: d.email || '', intro: d.intro || '', mapEmbed: d.mapEmbed || '', formEnabled: d.formEnabled !== false });
    }
    const loaderItem = (cmsItems || []).find(i => i.type === 'loader');
    if (loaderItem) {
      const l = loaderItem.contentJSON || {};
      setLoaderForm({
        enabled: !!l.enabled,
        spinner: l.spinner || '',
        spinnerLight: l.spinnerLight || l.spinner || '',
        spinnerDark: l.spinnerDark || l.spinner || '',
        message: l.message || 'Loading...',
        backgroundColor: l.backgroundColor || '#ffffff',
        backgroundColorLight: l.backgroundColorLight || l.backgroundColor || '#ffffff',
        backgroundColorDark: l.backgroundColorDark || l.backgroundColor || '#0b1220',
        durationMs: l.durationMs || 800,
        customJS: l.customJS || ''
      });
    }
    // initialize landing backgrounds
    const landingItem = (cmsItems || []).find(i => i.type === 'landing');
    if (landingItem) {
      setLandingForm({ backgrounds: landingItem.contentJSON?.backgrounds || {} });
    }
  }, [cmsItems]);

  // Load venues when switching to Venues tab
  useEffect(() => {
    if (tab === 4) {
      setVenueLoading(true);
      api.get('/api/venues')
        .then(res => setVenues(res.data || []))
        .catch(() => setVenues([]))
        .finally(() => setVenueLoading(false));
    }
  }, [tab]);
  // Schedule handlers
  const openScheduleDialog = (idx = null) => {
    setScheduleEditIdx(idx);
    setScheduleForm(idx !== null ? { ...schedule[idx] } : { time: '', activity: '' });
    setScheduleDialog(true);
  };
  const saveScheduleItem = () => {
    if (!scheduleForm.time.trim() || !scheduleForm.activity.trim()) return;
    let newSchedule = [...schedule];
    if (scheduleEditIdx !== null) {
      newSchedule[scheduleEditIdx] = { ...scheduleForm };
    } else {
      newSchedule.push({ ...scheduleForm });
    }
    setSchedule(newSchedule);
    setScheduleDialog(false);
    setScheduleEditIdx(null);
    setScheduleForm({ time: '', activity: '' });
  };
  const deleteScheduleItem = idx => {
    setSchedule(schedule.filter((_, i) => i !== idx));
  };
  const saveScheduleToBackend = async () => {
    setScheduleLoading(true);
    try {
      // Find all schedule CMS items
      const scheduleItems = cmsItems.filter(i => i.type === 'schedule');
      let saved = false;
      if (scheduleItems.length > 0) {
        // Try to update the first one
        try {
          await api.put(`/api/cms/${scheduleItems[0]._id}`, { contentJSON: { items: schedule } });
          saved = true;
        } catch (err) {
          // If not found, fallback to POST
          if (err.response && err.response.data && /not found/i.test(err.response.data.error)) {
            await api.post('/api/cms', { type: 'schedule', contentJSON: { items: schedule } });
            saved = true;
          } else {
            throw err;
          }
        }
        // Optionally, warn if more than one schedule CMS item exists
        if (scheduleItems.length > 1) {
          setSnackbar({ open: true, message: 'Warning: Multiple schedule CMS items exist. Please clean up duplicates.', severity: 'warning' });
        }
      } else {
        // No schedule CMS item, create new
        await api.post('/api/cms', { type: 'schedule', contentJSON: { items: schedule } });
        saved = true;
      }
      if (saved) {
        // Refresh CMS items and schedule state
        const res = await api.get('/api/cms');
        setCmsItems(res.data);
        const sched = (res.data || []).find(i => i.type === 'schedule');
        setSchedule(sched?.contentJSON?.items || []);
        setSnackbar({ open: true, message: 'Schedule saved!', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save schedule', severity: 'error' });
    } finally {
      setScheduleLoading(false);
    }
  };

  // Save Footer content to CMS (type: 'footer')
  const saveFooterToBackend = async () => {
    setFooterLoading(true);
    try {
      const footerItem = (cmsItems || []).find(i => i.type === 'footer');
      const payload = { contentJSON: { ...footerForm, links: Array.isArray(footerForm.links) ? footerForm.links : [], social: Array.isArray(footerForm.social) ? footerForm.social : [] } };
      if (footerItem && footerItem._id) {
        await api.put(`/api/cms/${footerItem._id}`, payload);
      } else {
        await api.post('/api/cms', { type: 'footer', ...payload });
      }
      const res = await api.get('/api/cms');
      setCmsItems(res.data);
      setSnackbar({ open: true, message: 'Footer saved!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save footer', severity: 'error' });
    } finally {
      setFooterLoading(false);
    }
  };

  // Save Contact page content to CMS (type: 'contact')
  const saveContactToBackend = async () => {
    setContactLoading(true);
    try {
      const contactItem = (cmsItems || []).find(i => i.type === 'contact');
      const payload = { contentJSON: { ...contactForm } };
      if (contactItem && contactItem._id) {
        await api.put(`/api/cms/${contactItem._id}`, payload);
      } else {
        await api.post('/api/cms', { type: 'contact', ...payload });
      }
      const res = await api.get('/api/cms');
      setCmsItems(res.data);
      setSnackbar({ open: true, message: 'Contact page saved!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save contact page', severity: 'error' });
    } finally {
      setContactLoading(false);
    }
  };

  // Save Landing backgrounds to CMS (type: 'landing')
  const saveLandingToBackend = async () => {
    setLandingLoading(true);
    try {
      const current = await api.get('/api/cms');
      const landingItem = (current.data || current).find(i => i.type === 'landing');
      const payload = { contentJSON: { backgrounds: landingForm.backgrounds || {} } };
      if (landingItem && landingItem._id) {
        await api.put(`/api/cms/${landingItem._id}`, payload);
      } else {
        try {
          await api.post('/api/cms', { type: 'landing', ...payload });
        } catch (err) {
          if (err.response && err.response.status === 409) {
            const fresh = await api.get('/api/cms');
            const freshLanding = (fresh.data || fresh).find(i => i.type === 'landing');
            if (freshLanding && freshLanding._id) {
              await api.put(`/api/cms/${freshLanding._id}`, payload);
            } else {
              throw err;
            }
          } else throw err;
        }
      }
      const res = await api.get('/api/cms');
      setCmsItems(res.data);
      setSnackbar({ open: true, message: 'Landing backgrounds saved!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save landing backgrounds', severity: 'error' });
    } finally {
      setLandingLoading(false);
    }
  };

  // Fetch sliders for Sliders tab
  useEffect(() => {
    if (tab === 0) {
      setSliderLoading(true);
      api.get('/api/cms/sliders')
        .then(res => setSliders(res.data))
        .catch(() => setSliders([]))
        .finally(() => setSliderLoading(false));
      // Fetch slider area config and belt messages
      api.get('/api/cms/slider-config').then(res => {
        setSliderWidth(res.data.width || 1200);
        setSliderHeight(res.data.height || 420);
        setCountdownDate(res.data.countdownDate || '2026-06-01T09:00');
        setBeltMessages(res.data.beltMessages || []);
      }).catch(() => {
        setSliderWidth(1200);
        setSliderHeight(420);
        setCountdownDate('2026-06-01T09:00');
        setBeltMessages([]);
      });
    }
  }, [tab]);

  // Handle slider image upload (simulate, replace with real upload if needed)
  const handleSliderImageChange = e => {
    const file = e.target.files[0];
    setSliderImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = ev => setSliderForm(f => ({ ...f, image: ev.target.result }));
      reader.readAsDataURL(file);
    }
  };

  // Open dialog for add/edit
  const openSliderDialog = (slider = null) => {
    setSliderEdit(slider);
    setSliderForm(slider ? { ...slider.contentJSON } : { title: '', desc: '', image: '', style: '' });
    setSliderDialog(true);
  };

  // Save slider (add or edit)
  const saveSlider = async () => {
    setSliderLoading(true);
    try {
      if (sliderEdit) {
        // Edit
        const res = await api.put(`/api/cms/sliders/${sliderEdit._id}`, { contentJSON: sliderForm });
        setSliders(sliders.map(s => s._id === sliderEdit._id ? res.data : s));
        setSnackbar({ open: true, message: 'Slider updated', severity: 'success' });
      } else {
        // Add
        const res = await api.post('/api/cms/sliders', { contentJSON: sliderForm });
        setSliders([...sliders, res.data]);
        setSnackbar({ open: true, message: 'Slider added', severity: 'success' });
      }
      setSliderDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Save failed', severity: 'error' });
    } finally {
      setSliderLoading(false);
    }
  };

  // Delete slider
  const deleteSlider = async id => {
    setSliderLoading(true);
    try {
      await api.delete(`/api/cms/sliders/${id}`);
      setSliders(sliders.filter(s => s._id !== id));
      setSnackbar({ open: true, message: 'Slider deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Delete failed', severity: 'error' });
    } finally {
      setSliderLoading(false);
    }
  };

  // About tab content
  const renderAboutTab = () => (
    <Box sx={{ maxWidth: 650, mx: 'auto', mt: 2, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 2, p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Edit About Us Page</Typography>
      <TextField
        label="Heading"
        fullWidth
        value={aboutForm.heading}
        onChange={e => setAboutForm(f => ({ ...f, heading: e.target.value }))}
        sx={{ mb: 2 }}
      />
      <TextField
        label="About Text"
        multiline
        minRows={3}
        fullWidth
        value={aboutForm.text}
        onChange={e => setAboutForm(f => ({ ...f, text: e.target.value }))}
        sx={{ mb: 2 }}
      />
      <TextField
        label="Image URL"
        fullWidth
        value={aboutForm.image}
        onChange={e => setAboutForm(f => ({ ...f, image: e.target.value }))}
        sx={{ mb: 2 }}
      />
      <TextField
        label="Link (optional)"
        fullWidth
        value={aboutForm.link}
        onChange={e => setAboutForm(f => ({ ...f, link: e.target.value }))}
        sx={{ mb: 2 }}
      />
      {/* Live Preview */}
      <Box sx={{ mt: 3, mb: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default', color: 'text.primary' }}>
        <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 1 }}>{aboutForm.heading || 'About Us Heading'}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{aboutForm.text || 'About us description will appear here.'}</Typography>
        {aboutForm.image && (
          <img src={fixAssetUrl(aboutForm.image)} alt="About preview" style={{ width: '100%', maxWidth: 320, borderRadius: 8, marginBottom: 16, boxShadow: 2 }} />
        )}
        {aboutForm.link && (
          <Button href={aboutForm.link} target="_blank" rel="noopener" variant="outlined" color="secondary" sx={{ mt: 1 }}>Learn More</Button>
        )}
      </Box>
      <Button variant="contained" color="primary" onClick={saveAboutToBackend} disabled={aboutLoading}>
        {aboutLoading ? 'Saving...' : 'Save'}
      </Button>
    </Box>
  );

  // Save About content to CMS
  const saveAboutToBackend = async () => {
    setAboutLoading(true);
    try {
      // Check if About item exists
      const current = await api.get('/api/cms');
      const aboutItem = (current.data || current).find(i => i.type === 'about');
      const payload = { contentJSON: { ...aboutForm } };
      if (aboutItem && aboutItem._id) {
        await api.put(`/api/cms/${aboutItem._id}`, payload);
      } else {
        try {
          await api.post('/api/cms', { type: 'about', ...payload });
        } catch (err) {
          if (err.response && err.response.status === 409) {
            const fresh = await api.get('/api/cms');
            const freshAbout = (fresh.data || fresh).find(i => i.type === 'about');
            if (freshAbout && freshAbout._id) {
              await api.put(`/api/cms/${freshAbout._id}`, payload);
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        }
      }
      setSnackbar({ open: true, message: 'About page saved!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save About page', severity: 'error' });
    } finally {
      setAboutLoading(false);
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          CMS Management
        </Typography>
        <NotificationSnackbar {...snackbar} onClose={() => setSnackbar(s => ({ ...s, open: false }))} />
        <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
          {cmsTabs.map((t, i) => <Tab key={t} label={t} />)}
        </Tabs>
        {tab === 0 && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="contained" color="primary" onClick={() => openSliderDialog()} sx={{ minWidth: 140 }}>Add Slide</Button>
              <TextField 
                label="Slider Width (px)"
                type="number"
                value={sliderWidth}
                onChange={e => setSliderWidth(Number(e.target.value))}
                sx={{ width: 160 }}
                inputProps={{ min: 600, max: 2400 }}
                helperText="Recommended: 1200-1800px"
              />
              <TextField 
                label="Slider Height (px)"
                type="number"
                value={sliderHeight}
                onChange={e => setSliderHeight(Number(e.target.value))}
                sx={{ width: 160 }}
                inputProps={{ min: 300, max: 900 }}
                helperText="Recommended: 400-600px"
              />
              <Tooltip title="Save slider area size and settings">
                <Button variant="outlined" color="success" onClick={async () => {
                  await api.post('/api/cms/slider-config', { width: sliderWidth, height: sliderHeight, countdownDate, beltMessages });
                  setSnackbar({ open: true, message: 'Slider area settings saved!', severity: 'success' });
                }}>Save Area</Button>
              </Tooltip>
              <Tooltip title="Reset to recommended size">
                <Button variant="text" color="secondary" onClick={() => { setSliderWidth(1200); setSliderHeight(420); }}>Reset</Button>
              </Tooltip>
              <Box sx={{ ml: 2, p: 1, bgcolor: '#f5faff', borderRadius: 2, boxShadow: 1, minWidth: 220, maxWidth: 400, width: { xs: '100%', sm: 320, md: 400 }, mx: 'auto' }}>
                <Typography variant="caption" color="primary">Live Preview</Typography>
                <Box sx={{ width: '100%', height: sliderHeight/3, bgcolor: '#e6f0ff', borderRadius: 2, mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 1, position: 'relative', overflow: 'hidden' }}>
                  <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,#e6f0ff 60%,#fff 100%)', opacity: 0.7 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ zIndex: 2 }}>{sliderWidth} x {sliderHeight}px</Typography>
                  <Box sx={{ position: 'absolute', bottom: 8, left: 0, width: '100%', px: 2, textAlign: 'center', zIndex: 2 }}>
                    <Typography variant="caption" color="primary">Slider Title</Typography>
                    <Typography variant="body2" color="text.secondary">Slider description preview...</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                label="Countdown Date/Time"
                type="datetime-local"
                value={countdownDate}
                onChange={e => setCountdownDate(e.target.value)}
                sx={{ width: 260 }}
                InputLabelProps={{ shrink: true }}
                helperText="Set event start date/time"
              />
              <Tooltip title="Save countdown date/time">
                <Button variant="outlined" color="success" onClick={async () => {
                  await api.post('/api/cms/slider-config', { width: sliderWidth, height: sliderHeight, countdownDate, beltMessages });
                  setSnackbar({ open: true, message: 'Countdown updated!', severity: 'success' });
                }}>Save Countdown</Button>
              </Tooltip>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Rotating Belt Messages</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField label="Add Belt Message" value={beltInput} onChange={e => setBeltInput(e.target.value)} sx={{ flex: 1 }} helperText="Short message for homepage belt" />
                <Button variant="contained" color="primary" onClick={() => {
                  if (beltInput.trim()) setBeltMessages(msgs => [...msgs, beltInput.trim()]); setBeltInput('');
                }}>Add</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {beltMessages.map((msg, i) => (
                  <Box key={i} sx={{ bgcolor: '#e6f0ff', color: '#007bff', px: 2, py: 0.5, borderRadius: 2, display: 'flex', alignItems: 'center', mb: 1 }}>
                    <span>{msg}</span>
                    <Button size="small" color="info" sx={{ ml: 1, minWidth: 0, px: 1 }} onClick={() => setBeltInput(msg)}>Edit</Button>
                    <Button size="small" color="error" sx={{ ml: 1, minWidth: 0, px: 1 }} onClick={() => setBeltMessages(msgs => msgs.filter((_, idx) => idx !== i))}>Delete</Button>
                    {i > 0 && <Button size="small" color="secondary" sx={{ ml: 1, minWidth: 0, px: 1 }} onClick={() => {
                      setBeltMessages(msgs => {
                        const arr = [...msgs];
                        [arr[i-1], arr[i]] = [arr[i], arr[i-1]];
                        return arr;
                      });
                    }}>↑</Button>}
                    {i < beltMessages.length-1 && <Button size="small" color="secondary" sx={{ ml: 1, minWidth: 0, px: 1 }} onClick={() => {
                      setBeltMessages(msgs => {
                        const arr = [...msgs];
                        [arr[i], arr[i+1]] = [arr[i+1], arr[i]];
                        return arr;
                      });
                    }}>↓</Button>}
                  </Box>
                ))}
              </Box>
              <Button variant="outlined" color="success" sx={{ mt: 1 }} onClick={async () => {
                await api.post('/api/cms/slider-config', { width: sliderWidth, height: sliderHeight, countdownDate, beltMessages });
                setSnackbar({ open: true, message: 'Belt messages saved!', severity: 'success' });
              }}>Save Belt</Button>
              <Box sx={{ mt: 2, p: 1, bgcolor: '#f5faff', borderRadius: 2, boxShadow: 1, minWidth: 220 }}>
                <Typography variant="caption" color="primary">Live Belt Preview</Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {beltMessages.map((msg, i) => (
                    <Box key={i} sx={{ bgcolor: '#e6f0ff', color: '#007bff', px: 2, py: 0.5, borderRadius: 2, mb: 1 }}>{msg}</Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </>
        )}
        {tab === 1 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={() => openScheduleDialog()}>Add Schedule Item</Button>
              <Button variant="outlined" color="success" onClick={saveScheduleToBackend} disabled={scheduleLoading}>
                {scheduleLoading ? 'Saving...' : 'Save Schedule'}
              </Button>
            </Box>
            <Table sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden', mb: 2 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.paper' }}>
                  <TableCell>Time</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedule.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{item.time}</TableCell>
                    <TableCell>{item.activity}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => openScheduleDialog(idx)}>Edit</Button>
                      <Button size="small" color="error" variant="outlined" sx={{ ml: 1 }} onClick={() => deleteScheduleItem(idx)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {schedule.length === 0 && (
                  <TableRow><TableCell colSpan={3} align="center">No schedule items</TableCell></TableRow>
                )}
                
              </TableBody>
            </Table>
            <Dialog open={scheduleDialog} onClose={() => setScheduleDialog(false)} maxWidth="xs" fullWidth>
              <DialogTitle>{scheduleEditIdx !== null ? 'Edit Schedule Item' : 'Add Schedule Item'}</DialogTitle>
              <DialogContent>
                <TextField
                  label="Time"
                  value={scheduleForm.time}
                  onChange={e => setScheduleForm(f => ({ ...f, time: e.target.value }))}
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="e.g. 09:00 AM"
                />
                <TextField
                  label="Activity"
                  value={scheduleForm.activity}
                  onChange={e => setScheduleForm(f => ({ ...f, activity: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Describe the activity"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setScheduleDialog(false)} color="primary">Cancel</Button>
                <Button onClick={saveScheduleItem} color="success" variant="contained" disabled={!scheduleForm.time.trim() || !scheduleForm.activity.trim()}>{scheduleEditIdx !== null ? 'Update' : 'Add'}</Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        {tab === 2 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={() => openHighlightDialog()}>Add Highlight</Button>
              <Button variant="outlined" color="success" onClick={saveHighlightsToBackend} disabled={highlightsLoading}>
                {highlightsLoading ? 'Saving...' : 'Save Highlights'}
              </Button>
            </Box>
            <Table sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden', mb: 2 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.paper' }}>
                  <TableCell>Image</TableCell>
                  <TableCell>Heading</TableCell>
                  <TableCell>Description/Story</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {highlights.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center">No highlights</TableCell></TableRow>
                ) : highlights.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{item.image ? <img src={item.image} alt="highlight" style={{ width: 80, borderRadius: 8 }} /> : '-'}</TableCell>
                    <TableCell>{item.heading}</TableCell>
                    <TableCell>{item.story}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => openHighlightDialog(idx)}>Edit</Button>
                      <Button size="small" color="error" variant="outlined" sx={{ ml: 1 }} onClick={() => deleteHighlightItem(idx)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Dialog open={highlightDialog} onClose={() => setHighlightDialog(false)} maxWidth="xs" fullWidth>
              <DialogTitle>{highlightEditIdx !== null ? 'Edit Highlight' : 'Add Highlight'}</DialogTitle>
              <DialogContent>
                <TextField
                  label="Image Link (URL)"
                  value={highlightForm.image}
                  onChange={e => setHighlightForm(f => ({ ...f, image: e.target.value }))}
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="Paste image URL here"
                />
                <TextField
                  label="Heading"
                  value={highlightForm.heading}
                  onChange={e => setHighlightForm(f => ({ ...f, heading: e.target.value }))}
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="Highlight heading/title"
                />
                <TextField
                  label="Description / Story"
                  value={highlightForm.story}
                  onChange={e => setHighlightForm(f => ({ ...f, story: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Write the highlight story or description (no HTML)"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setHighlightDialog(false)} color="primary">Cancel</Button>
                <Button onClick={saveHighlightItem} color="success" variant="contained" disabled={!highlightForm.image.trim() || !highlightForm.heading.trim() || !highlightForm.story.trim()}>{highlightEditIdx !== null ? 'Update' : 'Add'}</Button>
              </DialogActions>
            </Dialog>
            </>
          )}
          {tab === 4 && (
            <>
              <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                <Button variant="contained" color="primary" onClick={() => openVenueDialog()}>Add Venue</Button>
              </Box>
              {venueLoading ? (
                <Typography>Loading venues...</Typography>
              ) : (
                <Table sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden', mb: 2 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.paper' }}>
                      <TableCell>Image</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Capacity</TableCell>
                      <TableCell>Staff</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {venues.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No venues</TableCell></TableRow>
                    ) : venues.map(v => (
                      <TableRow key={v._id}>
                        <TableCell>{v.image ? <img src={v.image} alt="venue" style={{ width: 80, borderRadius: 8 }} /> : '-'}</TableCell>
                        <TableCell>{v.name}</TableCell>
                        <TableCell>{v.location}</TableCell>
                        <TableCell>{v.capacity || '-'}</TableCell>
                        <TableCell>{v.staff?.length || 0}</TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined" onClick={() => openVenueDialog(venues.findIndex(x=>x._id===v._id))}>Edit</Button>
                          <Button size="small" color="error" variant="outlined" sx={{ ml: 1 }} onClick={() => deleteVenueItem(v._id)}>Delete</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Dialog open={venueDialog} onClose={() => setVenueDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{venueEditIdx !== null ? 'Edit Venue' : 'Add Venue'}</DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label="Name" value={venueForm.name} onChange={e => setVenueForm(f=>({...f, name: e.target.value}))} fullWidth />
                    <TextField label="Location" value={venueForm.location} onChange={e => setVenueForm(f=>({...f, location: e.target.value}))} fullWidth />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField label="Latitude" value={venueForm.coords.lat || ''} onChange={e => setVenueForm(f=>({...f, coords: {...f.coords, lat: e.target.value}}))} />
                      <TextField label="Longitude" value={venueForm.coords.lng || ''} onChange={e => setVenueForm(f=>({...f, coords: {...f.coords, lng: e.target.value}}))} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="venue-image-upload"
                        type="file"
                        onChange={async e => {
                          const file = e.target.files?.[0]; if (!file) return; try { const fd = new FormData(); fd.append('file', file); const res = await api.post('/api/storage/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); if (res.data?.url) setVenueForm(f=>({...f, image: res.data.url})); else { const reader = new FileReader(); reader.onload = ev => setVenueForm(f=>({...f, image: ev.target.result})); reader.readAsDataURL(file); } } catch (err) { const reader = new FileReader(); reader.onload = ev => setVenueForm(f=>({...f, image: ev.target.result})); reader.readAsDataURL(file); } }} />
                      <label htmlFor="venue-image-upload"><Button component="span" variant="outlined" size="small">Upload Image</Button></label>
                      <TextField label="Image Link (URL)" value={venueForm.image} onChange={e=>setVenueForm(f=>({...f, image: e.target.value}))} fullWidth placeholder="Paste image URL here" />
                    </Box>
                    {venueForm.image && <Box><Typography variant="caption">Preview:</Typography><Box sx={{ mt: 1 }}><img src={venueForm.image} alt="preview" style={{ width: 180, borderRadius: 8 }} /></Box></Box>}
                    <TextField label="Capacity" type="number" value={venueForm.capacity} onChange={e=>setVenueForm(f=>({...f, capacity: e.target.value}))} sx={{ width: 160 }} />
                    <TextField label="Tags (comma separated)" value={(venueForm.tags||[]).join(',')} onChange={e=>setVenueForm(f=>({...f, tags: e.target.value.split(',').map(t=>t.trim()).filter(Boolean)}))} fullWidth />
                    <TextField label="Description" value={venueForm.description} onChange={e=>setVenueForm(f=>({...f, description: e.target.value}))} fullWidth multiline minRows={3} />

                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2">Staff</Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <TextField label="Name" value={staffEntry.name} onChange={e=>setStaffEntry(s=>({...s, name: e.target.value}))} />
                        <TextField label="Role" value={staffEntry.role} onChange={e=>setStaffEntry(s=>({...s, role: e.target.value}))} />
                        <TextField label="Email" value={staffEntry.email} onChange={e=>setStaffEntry(s=>({...s, email: e.target.value}))} />
                        <Button variant="contained" onClick={addStaffToVenueForm}>Add</Button>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                        {(venueForm.staff||[]).map((s, idx) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{s.role} — {s.email}</Typography>
                            </Box>
                            <Button size="small" color="error" onClick={() => removeStaffFromVenueForm(idx)}>Remove</Button>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setVenueDialog(false)} color="primary">Cancel</Button>
                  <Button onClick={saveVenueItem} color="success" variant="contained">{venueEditIdx !== null ? 'Update' : 'Add'}</Button>
                </DialogActions>
                </Dialog>
              </>
            )}
        {tab === 5 && <SponsorsTab />}
        {tab === 6 && <ContactTab />}
        {tab === 7 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={saveFooterToBackend} disabled={footerLoading}>{footerLoading ? 'Saving...' : 'Save Footer'}</Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <input accept="image/*" style={{ display: 'none' }} id="footer-logo-upload" type="file" onChange={async e => {
                  const file = e.target.files?.[0]; if (!file) return; try { const fd = new FormData(); fd.append('file', file); const res = await api.post('/api/storage/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); if (res.data?.url) setFooterForm(f=>({...f, logo: res.data.url})); else { const reader = new FileReader(); reader.onload = ev => setFooterForm(f=>({...f, logo: ev.target.result})); reader.readAsDataURL(file); } } catch (err) { const reader = new FileReader(); reader.onload = ev => setFooterForm(f=>({...f, logo: ev.target.result})); reader.readAsDataURL(file); } }} />
                <label htmlFor="footer-logo-upload"><Button component="span" variant="outlined" size="small">Upload Logo</Button></label>
                <TextField label="Logo Link (URL)" value={footerForm.logo} onChange={e=>setFooterForm(f=>({...f, logo: e.target.value}))} fullWidth />
              </Box>
              {footerForm.logo && <Box><Typography variant="caption">Logo preview:</Typography><Box sx={{ mt: 1 }}><img src={footerForm.logo} alt="logo" style={{ height: 64 }} /></Box></Box>}
              <TextField label="Footer Text" value={footerForm.text} onChange={e=>setFooterForm(f=>({...f, text: e.target.value}))} fullWidth multiline minRows={3} />
              <TextField label="Links (one per line, label|url)" value={(footerForm.links||[]).join('\n')} onChange={e=>setFooterForm(f=>({...f, links: e.target.value.split('\n').map(l=>l.trim()).filter(Boolean)}))} fullWidth multiline minRows={3} />
              <TextField label="Social Links (one per line, platform|url)" value={(footerForm.social||[]).join('\n')} onChange={e=>setFooterForm(f=>({...f, social: e.target.value.split('\n').map(l=>l.trim()).filter(Boolean)}))} fullWidth multiline minRows={2} />
              <TextField label="GFooter HTML / Content" value={footerForm.gfooterHTML} onChange={e=>setFooterForm(f=>({...f, gfooterHTML: e.target.value}))} fullWidth multiline minRows={4} helperText="Optional HTML or structured content for the greater footer area" />
            </Box>
          </>
        )}
        {tab === 7 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={saveLoaderToBackend} disabled={loaderLoading}>{loaderLoading ? 'Saving...' : 'Save Loader'}</Button>
              <Button variant="outlined" onClick={() => setPreviewOpen(true)}>Preview Loader</Button>
              <Button variant="text" onClick={() => setPreviewOpen(false)}>Close Preview</Button>
            </Box>
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
              <DialogTitle>Loader Preview (Light & Dark)</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', justifyContent: 'center', p: 1 }}>
                  <Box sx={{ flex: 1, borderRadius: 1, overflow: 'hidden', boxShadow: 1, position: 'relative' }}>
                    <Typography variant="subtitle2" sx={{ p: 1, bgcolor: 'grey.100' }}>Light Preview</Typography>
                    <LoaderOverlay overrideLoader={loaderForm} overrideOpen={true} forceMode="light" />
                  </Box>
                  <Box sx={{ flex: 1, borderRadius: 1, overflow: 'hidden', boxShadow: 1, position: 'relative' }}>
                    <Typography variant="subtitle2" sx={{ p: 1, bgcolor: 'grey.900', color: '#fff' }}>Dark Preview</Typography>
                    <LoaderOverlay overrideLoader={loaderForm} overrideOpen={true} forceMode="dark" />
                  </Box>
                </Box>
              </DialogContent>
            </Dialog>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <FormControlLabel control={<Switch checked={loaderForm.enabled} onChange={e=>setLoaderForm(f=>({...f, enabled: e.target.checked}))} />} label="Enable global loader" />
              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                <Typography variant="subtitle2">Spinner / Icon</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl sx={{ minWidth: 180, mr: 1 }} size="small">
                      <InputLabel id="loader-style-label">Preset</InputLabel>
                      <Select labelId="loader-style-label" value={loaderForm.preset || 'default'} label="Preset" onChange={e => {
                        const preset = e.target.value;
                        // Apply some sensible defaults per preset
                        const presets = {
                          default: { backgroundColorLight: '#ffffff', backgroundColorDark: '#0b1220', spinner: '', spinnerLight: '', spinnerDark: '' },
                          darkCentered: { backgroundColorLight: '#f5f5f5', backgroundColorDark: '#0b1220', spinner: '', spinnerLight: '', spinnerDark: '' },
                          minimal: { backgroundColorLight: '#ffffff', backgroundColorDark: '#111', spinner: '', spinnerLight: '', spinnerDark: '' },
                          branded: { backgroundColorLight: loaderForm.backgroundColorLight || '#ffffff', backgroundColorDark: loaderForm.backgroundColorDark || '#0b1220' }
                        };
                        setLoaderForm(f => ({ ...f, ...presets[preset], preset }));
                      }}>
                        <MenuItem value="default">Default</MenuItem>
                        <MenuItem value="darkCentered">Dark Centered</MenuItem>
                        <MenuItem value="minimal">Minimal</MenuItem>
                        <MenuItem value="branded">Branded</MenuItem>
                      </Select>
                    </FormControl>
                  <input accept="image/*" style={{ display: 'none' }} id="loader-spinner-upload-light" type="file" onChange={async e => {
                    const file = e.target.files?.[0]; if (!file) return; try { const fd = new FormData(); fd.append('file', file); const res = await api.post('/api/storage/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); if (res.data?.url) setLoaderForm(f=>({...f, spinnerLight: res.data.url})); else { const reader = new FileReader(); reader.onload = ev => setLoaderForm(f=>({...f, spinnerLight: ev.target.result})); reader.readAsDataURL(file); } } catch (err) { const reader = new FileReader(); reader.onload = ev => setLoaderForm(f=>({...f, spinnerLight: ev.target.result})); reader.readAsDataURL(file); } }} />
                  <label htmlFor="loader-spinner-upload-light"><Button component="span" variant="outlined" size="small">Upload Spinner (Light)</Button></label>
                  <TextField label="Spinner URL (Light)" value={loaderForm.spinnerLight} onChange={e=>setLoaderForm(f=>({...f, spinnerLight: e.target.value}))} fullWidth />
                </Box>
                {loaderForm.spinnerLight && <Box><Typography variant="caption">Light spinner preview:</Typography><Box sx={{ mt: 1 }}><img src={loaderForm.spinnerLight} alt="spinner-light" style={{ height: 64 }} /></Box></Box>}

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                  <input accept="image/*" style={{ display: 'none' }} id="loader-spinner-upload-dark" type="file" onChange={async e => {
                    const file = e.target.files?.[0]; if (!file) return; try { const fd = new FormData(); fd.append('file', file); const res = await api.post('/api/storage/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); if (res.data?.url) setLoaderForm(f=>({...f, spinnerDark: res.data.url})); else { const reader = new FileReader(); reader.onload = ev => setLoaderForm(f=>({...f, spinnerDark: ev.target.result})); reader.readAsDataURL(file); } } catch (err) { const reader = new FileReader(); reader.onload = ev => setLoaderForm(f=>({...f, spinnerDark: ev.target.result})); reader.readAsDataURL(file); } }} />
                  <label htmlFor="loader-spinner-upload-dark"><Button component="span" variant="outlined" size="small">Upload Spinner (Dark)</Button></label>
                  <TextField label="Spinner URL (Dark)" value={loaderForm.spinnerDark} onChange={e=>setLoaderForm(f=>({...f, spinnerDark: e.target.value}))} fullWidth />
                </Box>
                {loaderForm.spinnerDark && <Box><Typography variant="caption">Dark spinner preview:</Typography><Box sx={{ mt: 1 }}><img src={loaderForm.spinnerDark} alt="spinner-dark" style={{ height: 64 }} /></Box></Box>}

                <TextField label="Loader Message" value={loaderForm.message} onChange={e=>setLoaderForm(f=>({...f, message: e.target.value}))} fullWidth sx={{ mt: 1 }} />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField label="Background Color (Light)" value={loaderForm.backgroundColorLight} onChange={e=>setLoaderForm(f=>({...f, backgroundColorLight: e.target.value}))} sx={{ width: 220 }} />
                  <TextField label="Background Color (Dark)" value={loaderForm.backgroundColorDark} onChange={e=>setLoaderForm(f=>({...f, backgroundColorDark: e.target.value}))} sx={{ width: 220 }} />
                </Box>
              </Box>
              <TextField label="Auto-close duration (ms)" type="number" value={loaderForm.durationMs} onChange={e=>setLoaderForm(f=>({...f, durationMs: Number(e.target.value)}))} sx={{ width: 220 }} />
              <TextField label="Custom JS (optional)" value={loaderForm.customJS} onChange={e=>setLoaderForm(f=>({...f, customJS: e.target.value}))} fullWidth multiline minRows={4} helperText="Custom JS executed when loader shown (use with caution)" />
            </Box>
          </>
        )}
        {/* Contact tab is rendered by `ContactTab` component above. Removed duplicate inline form. */}
        {tab === 3 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={() => openVipDialog()}>Add VIP / Speaker</Button>
              <Button variant="outlined" color="success" onClick={saveVipsToBackend} disabled={vipLoading}>{vipLoading ? 'Saving...' : 'Save VIPs'}</Button>
            </Box>
            <Box sx={{ mb: 2 }}>
              {vips.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>No VIPs</Box>
              ) : (
                <DragDropContext onDragEnd={(result) => {
                  if (!result.destination) return;
                  const src = result.source.index;
                  const dst = result.destination.index;
                  const copy = Array.from(vips);
                  const [moved] = copy.splice(src, 1);
                  copy.splice(dst, 0, moved);
                  setVips(copy);
                }}>
                  <Droppable droppableId="vips-list">
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {vips.map((item, idx) => (
                          <Draggable key={idx} draggableId={`vip-${idx}`} index={idx}>
                            {(prov) => (
                              <Card ref={prov.innerRef} {...prov.draggableProps} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Box {...prov.dragHandleProps} sx={{ cursor: 'grab', px: 1 }}>≡</Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ width: 80, height: 56, overflow: 'hidden' }}>{item.image ? <img src={item.image} alt="vip" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} /> : '-'}</Box>
                                    <Box>
                                      <Typography variant="subtitle1" fontWeight={700}>{item.title || item.name}</Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{item.subtype || 'normal'} — {item.quote}</Typography>
                                    </Box>
                                  </Box>
                                </Box>
                                <Box>
                                  <Button size="small" variant="outlined" onClick={() => openVipDialog(idx)}>Edit</Button>
                                  <Button size="small" color="error" variant="outlined" sx={{ ml: 1 }} onClick={() => deleteVipItem(idx)}>Delete</Button>
                                </Box>
                              </Card>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </Box>

            <Dialog open={vipDialog} onClose={() => setVipDialog(false)} maxWidth="sm" fullWidth>
              <DialogTitle>{vipEditIdx !== null ? 'Edit VIP / Speaker' : 'Add VIP / Speaker'}</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="vip-subtype-label">Subtype</InputLabel>
                    <Select
                      labelId="vip-subtype-label"
                      value={vipForm.subtype}
                      label="Subtype"
                      onChange={e => setVipForm(f => ({ ...f, subtype: e.target.value }))}
                    >
                      <MenuItem value="main">Main VIP</MenuItem>
                      <MenuItem value="normal">VIP Guest</MenuItem>
                      <MenuItem value="speaker">Speaker</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="vip-image-upload"
                      type="file"
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const fd = new FormData();
                          fd.append('file', file);
                          // upload to backend storage
                          const res = await api.post('/api/storage/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                          if (res.data && res.data.url) {
                            setVipForm(f => ({ ...f, image: res.data.url }));
                          } else {
                            // fallback to dataURL preview
                            const reader = new FileReader();
                            reader.onload = ev => setVipForm(f => ({ ...f, image: ev.target.result }));
                            reader.readAsDataURL(file);
                          }
                        } catch (err) {
                          const reader = new FileReader();
                          reader.onload = ev => setVipForm(f => ({ ...f, image: ev.target.result }));
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <label htmlFor="vip-image-upload">
                      <Button component="span" variant="outlined" size="small">Upload Image</Button>
                    </label>
                    <TextField label="Image Link (URL)" value={vipForm.image} onChange={e => setVipForm(f => ({ ...f, image: e.target.value }))} fullWidth placeholder="Paste image URL here" />
                  </Box>
                  {vipForm.image && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption">Preview:</Typography>
                      <Box sx={{ mt: 1 }}>
                        <img src={vipForm.image} alt="preview" style={{ width: 120, borderRadius: vipForm.subtype === 'speaker' ? '50%' : 8 }} />
                      </Box>
                    </Box>
                  )}
                  <TextField label="Title" value={vipForm.title} onChange={e => setVipForm(f => ({ ...f, title: e.target.value }))} fullWidth placeholder="Title or role (for main)" />
                  <TextField label="Name" value={vipForm.name} onChange={e => setVipForm(f => ({ ...f, name: e.target.value }))} fullWidth placeholder="Person name" />
                  <TextField label="Quote" value={vipForm.quote} onChange={e => setVipForm(f => ({ ...f, quote: e.target.value }))} fullWidth placeholder="Short quote (optional)" />
                  <TextField label="Description" value={vipForm.description} onChange={e => setVipForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline minRows={3} placeholder="Bio or description" />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setVipDialog(false)} color="primary">Cancel</Button>
                <Button onClick={saveVipItem} color="success" variant="contained" disabled={!vipForm.title && !vipForm.name}>{vipEditIdx !== null ? 'Update' : 'Add'}</Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        {tab === 8 && renderAboutTab()}
        {tab ===  10 && (
          <Box sx={{ maxWidth: 800, mx: 'auto', mt: 2, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 2, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Landing Page Backgrounds (site sections)</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>Set per-page background image URLs for public pages. Contact and Venues backgrounds are managed in their respective tabs.</Typography>
            {['home','about','packages','accommodations','schedule','highlights','privacy','terms'].map(key => (
              <TextField
                key={key}
                label={`${key.charAt(0).toUpperCase() + key.slice(1)} Background URL`}
                fullWidth
                value={landingForm.backgrounds?.[key] || ''}
                onChange={e => setLandingForm(f => ({ ...f, backgrounds: { ...(f.backgrounds||{}), [key]: e.target.value } }))}
                sx={{ mb: 2 }}
              />
            ))}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={saveLandingToBackend} disabled={landingLoading}>{landingLoading ? 'Saving...' : 'Save Backgrounds'}</Button>
              <Button variant="outlined" onClick={async () => {
                const res = await api.get('/api/cms');
                setCmsItems(res.data);
                const landing = (res.data || []).find(i => i.type === 'landing');
                setLandingForm({ backgrounds: landing?.contentJSON?.backgrounds || {} });
                setSnackbar({ open: true, message: 'Reloaded landing settings', severity: 'info' });
              }}>Reload</Button>
            </Box>
          </Box>
        )}
        {tab === cmsTabs.length - 1 && (
          <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => setEditOpen(true)}>Create New Page</Button>
        )}
        {tab !== 0 && tab !== 1 && tab !== cmsTabs.length - 1 && (
          <Button variant="contained" color="primary" sx={{ mb: 2 }}>Add CMS Item</Button>
        )}
        {/* New Page Creation Dialog */}
        <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
          <DialogTitle>Create New Page</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Page Name (e.g. About, Contact, Gallery)"
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              variant="outlined"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)} color="primary">Cancel</Button>
            <Button onClick={() => {
              // Add logic to create new page in backend here
              setEditOpen(false);
              setSnackbar({ open: true, message: 'Page created (implement backend logic)', severity: 'success' });
            }} color="success" variant="contained" disabled={!editContent.trim()}>Create</Button>
          </DialogActions>
        </Dialog>
        <NotificationSnackbar
          open={snackbar.open}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          message={snackbar.message}
          severity={snackbar.severity}
        />
        {tab === 0 && (
          sliderLoading ? <Typography>Loading...</Typography> : (
            <Table sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.paper' }}>
                  <TableCell>Image</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Style</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sliders.map(slide => (
                  <TableRow key={slide._id}>
                    <TableCell>{slide.contentJSON.image ? <img src={slide.contentJSON.image} alt="slide" style={{ width: 80, borderRadius: 8 }} /> : '-'}</TableCell>
                    <TableCell>{slide.contentJSON.title}</TableCell>
                    <TableCell>{slide.contentJSON.desc}</TableCell>
                    <TableCell>{slide.contentJSON.style}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => openSliderDialog(slide)}>Edit</Button>
                      <Button size="small" color="error" variant="outlined" sx={{ ml: 1 }} onClick={() => deleteSlider(slide._id)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
        {/* Slider Add/Edit Dialog */}
        <Dialog open={sliderDialog} onClose={() => setSliderDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{sliderEdit ? 'Edit Slide' : 'Add Slide'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {sliderForm.image && <img src={sliderForm.image} alt="slide" style={{ width: 120, borderRadius: 8, boxShadow: 2 }} />}
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="slider-image-upload"
                    type="file"
                    onChange={handleSliderImageChange}
                  />
                  <label htmlFor="slider-image-upload">
                    <Tooltip title="Upload new image">
                      <IconButton color="primary" component="span" sx={{ border: '1px solid #ccc' }}>
                        <PhotoCamera />
                      </IconButton>
                    </Tooltip>
                  </label>
                </Box>
                <TextField
                  label="Image Link (URL)"
                  value={sliderForm.image}
                  onChange={e => setSliderForm(f => ({ ...f, image: e.target.value }))}
                  fullWidth
                  placeholder="Paste image URL here"
                  sx={{ mt: 1 }}
                  helperText="Use a direct image link or select below"
                />
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Choose from uploaded images</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {["about.jpg","sponsor-placeholder.png","sponsor1.png","sponsor2.png","venues.jpg","vip-placeholder.jpg","vip1.jpg","vip2.jpg","hero-bg.jpg","testimonial-user.jpg","logo.png"].map(img => (
                      <img
                        key={img}
                        src={img.startsWith('hero-bg') || img.startsWith('testimonial-user') ? `/images/${img}` : `/assets/${img}`}
                        alt={img}
                        style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: sliderForm.image.includes(img) ? '2px solid #007bff' : '1px solid #ccc', boxShadow: sliderForm.image.includes(img) ? 2 : 0 }}
                        onClick={() => setSliderForm(f => ({ ...f, image: img.startsWith('hero-bg') || img.startsWith('testimonial-user') ? `/images/${img}` : `/assets/${img}` }))}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
              <TextField
                label="Title"
                value={sliderForm.title}
                onChange={e => setSliderForm(f => ({ ...f, title: e.target.value }))}
                fullWidth
                helperText="Short headline for the slide"
              />
              <TextField
                label="Description"
                value={sliderForm.desc}
                onChange={e => setSliderForm(f => ({ ...f, desc: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
                helperText="Description or message for the slide"
              />
              <TextField
                label="Style (optional)"
                value={sliderForm.style}
                onChange={e => setSliderForm(f => ({ ...f, style: e.target.value }))}
                fullWidth
                helperText="Custom style or class for advanced design"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSliderDialog(false)} color="primary">Cancel</Button>
            <Button onClick={saveSlider} color="success" variant="contained" disabled={sliderLoading || !sliderForm.title || !sliderForm.image}>Save</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SuperAdminLayout>
  );
}

export default CMSManagement;
